export type LegalDocumentType = 'privacy' | 'terms';

type SectionBlockType = 'paragraph' | 'bullets' | 'numbers';

export interface SectionBlockDefinition {
  type: SectionBlockType;
  keys: string[];
}

export interface LegalSectionDefinition {
  id: string;
  titleKey: string;
  blocks: SectionBlockDefinition[];
}

export interface LegalDocumentDefinition {
  type: LegalDocumentType;
  heroTitleKey: string;
  heroSummaryKey: string;
  sections: LegalSectionDefinition[];
}

const privacySections: LegalSectionDefinition[] = [
  {
    id: 'scope',
    titleKey: 'legal.privacy.sections.scope.title',
    blocks: [
      {
        type: 'paragraph',
        keys: ['legal.privacy.sections.scope.body']
      }
    ]
  },
  {
    id: 'controller',
    titleKey: 'legal.privacy.sections.controller.title',
    blocks: [
      {
        type: 'paragraph',
        keys: ['legal.privacy.sections.controller.body']
      }
    ]
  },
  {
    id: 'dataCategories',
    titleKey: 'legal.privacy.sections.dataCategories.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.privacy.sections.dataCategories.items.account',
          'legal.privacy.sections.dataCategories.items.content',
          'legal.privacy.sections.dataCategories.items.billing',
          'legal.privacy.sections.dataCategories.items.logs',
          'legal.privacy.sections.dataCategories.items.regulatory'
        ]
      }
    ]
  },
  {
    id: 'legalBasis',
    titleKey: 'legal.privacy.sections.legalBasis.title',
    blocks: [
      {
        type: 'numbers',
        keys: [
          'legal.privacy.sections.legalBasis.items.contract',
          'legal.privacy.sections.legalBasis.items.legitimateInterest',
          'legal.privacy.sections.legalBasis.items.consent',
          'legal.privacy.sections.legalBasis.items.legalObligation'
        ]
      }
    ]
  },
  {
    id: 'purpose',
    titleKey: 'legal.privacy.sections.purpose.title',
    blocks: [
      {
        type: 'numbers',
        keys: [
          'legal.privacy.sections.purpose.items.accessControl',
          'legal.privacy.sections.purpose.items.delivery',
          'legal.privacy.sections.purpose.items.business',
          'legal.privacy.sections.purpose.items.optimization',
          'legal.privacy.sections.purpose.items.security'
        ]
      }
    ]
  },
  {
    id: 'sharing',
    titleKey: 'legal.privacy.sections.sharing.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.privacy.sections.sharing.items.vendors',
          'legal.privacy.sections.sharing.items.legal',
          'legal.privacy.sections.sharing.items.transfer',
          'legal.privacy.sections.sharing.items.anonymized'
        ]
      }
    ]
  },
  {
    id: 'contentLifecycle',
    titleKey: 'legal.privacy.sections.contentLifecycle.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.privacy.sections.contentLifecycle.items.storage',
          'legal.privacy.sections.contentLifecycle.items.cache',
          'legal.privacy.sections.contentLifecycle.items.deletion'
        ]
      }
    ]
  },
  {
    id: 'modelUsage',
    titleKey: 'legal.privacy.sections.modelUsage.title',
    blocks: [
      {
        type: 'paragraph',
        keys: ['legal.privacy.sections.modelUsage.body']
      },
      {
        type: 'bullets',
        keys: [
          'legal.privacy.sections.modelUsage.items.optimization',
          'legal.privacy.sections.modelUsage.items.optOut',
          'legal.privacy.sections.modelUsage.items.automation'
        ]
      }
    ]
  },
  {
    id: 'cookies',
    titleKey: 'legal.privacy.sections.cookies.title',
    blocks: [
      {
        type: 'paragraph',
        keys: ['legal.privacy.sections.cookies.body']
      }
    ]
  },
  {
    id: 'telemetry',
    titleKey: 'legal.privacy.sections.telemetry.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.privacy.sections.telemetry.items.destination',
          'legal.privacy.sections.telemetry.items.controls',
          'legal.privacy.sections.telemetry.items.scope'
        ]
      }
    ]
  },
  {
    id: 'retention',
    titleKey: 'legal.privacy.sections.retention.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.privacy.sections.retention.items.accounts',
          'legal.privacy.sections.retention.items.tokens',
          'legal.privacy.sections.retention.items.history',
          'legal.privacy.sections.retention.items.disposal'
        ]
      }
    ]
  },
  {
    id: 'crossBorder',
    titleKey: 'legal.privacy.sections.crossBorder.title',
    blocks: [
      {
        type: 'paragraph',
        keys: ['legal.privacy.sections.crossBorder.body']
      }
    ]
  },
  {
    id: 'rights',
    titleKey: 'legal.privacy.sections.rights.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.privacy.sections.rights.items.access',
          'legal.privacy.sections.rights.items.restrict',
          'legal.privacy.sections.rights.items.withdraw',
          'legal.privacy.sections.rights.items.complaint'
        ]
      },
      {
        type: 'paragraph',
        keys: ['legal.privacy.sections.rights.notice']
      }
    ]
  },
  {
    id: 'security',
    titleKey: 'legal.privacy.sections.security.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.privacy.sections.security.items.transport',
          'legal.privacy.sections.security.items.encryption',
          'legal.privacy.sections.security.items.audit',
          'legal.privacy.sections.security.items.abuse'
        ]
      }
    ]
  },
  {
    id: 'minors',
    titleKey: 'legal.privacy.sections.minors.title',
    blocks: [
      {
        type: 'paragraph',
        keys: ['legal.privacy.sections.minors.body']
      }
    ]
  },
  {
    id: 'updates',
    titleKey: 'legal.privacy.sections.updates.title',
    blocks: [
      {
        type: 'paragraph',
        keys: ['legal.privacy.sections.updates.body']
      }
    ]
  },
  {
    id: 'contact',
    titleKey: 'legal.privacy.sections.contact.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.privacy.sections.contact.items.email',
          'legal.privacy.sections.contact.items.mail'
        ]
      }
    ]
  }
];

const termsSections: LegalSectionDefinition[] = [
  {
    id: 'acceptance',
    titleKey: 'legal.terms.sections.acceptance.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.acceptance.items.age',
          'legal.terms.sections.acceptance.items.changes'
        ]
      }
    ]
  },
  {
    id: 'service',
    titleKey: 'legal.terms.sections.service.title',
    blocks: [
      {
        type: 'paragraph',
        keys: ['legal.terms.sections.service.body']
      }
    ]
  },
  {
    id: 'account',
    titleKey: 'legal.terms.sections.account.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.account.items.accuracy',
          'legal.terms.sections.account.items.credential',
          'legal.terms.sections.account.items.abuse'
        ]
      }
    ]
  },
  {
    id: 'quota',
    titleKey: 'legal.terms.sections.quota.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.quota.items.deviceTokens',
          'legal.terms.sections.quota.items.userTokens',
          'legal.terms.sections.quota.items.rateLimits',
          'legal.terms.sections.quota.items.breach'
        ]
      }
    ]
  },
  {
    id: 'acceptableUse',
    titleKey: 'legal.terms.sections.acceptableUse.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.acceptableUse.items.prohibitedContent',
          'legal.terms.sections.acceptableUse.items.reverseEngineering',
          'legal.terms.sections.acceptableUse.items.noTraining',
          'legal.terms.sections.acceptableUse.items.enforcement'
        ]
      }
    ]
  },
  {
    id: 'billing',
    titleKey: 'legal.terms.sections.billing.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.billing.items.pricing',
          'legal.terms.sections.billing.items.fallback',
          'legal.terms.sections.billing.items.disputes'
        ]
      }
    ]
  },
  {
    id: 'ip',
    titleKey: 'legal.terms.sections.ip.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.ip.items.userRights',
          'legal.terms.sections.ip.items.generatedUse',
          'legal.terms.sections.ip.items.platformRights'
        ]
      }
    ]
  },
  {
    id: 'availability',
    titleKey: 'legal.terms.sections.availability.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.availability.items.asIs',
          'legal.terms.sections.availability.items.forceMajeure',
          'legal.terms.sections.availability.items.quality',
          'legal.terms.sections.availability.items.userLiability'
        ]
      }
    ]
  },
  {
    id: 'liability',
    titleKey: 'legal.terms.sections.liability.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.liability.items.indirect'
        ]
      }
    ]
  },
  {
    id: 'indemnity',
    titleKey: 'legal.terms.sections.indemnity.title',
    blocks: [
      {
        type: 'paragraph',
        keys: ['legal.terms.sections.indemnity.body']
      }
    ]
  },
  {
    id: 'termination',
    titleKey: 'legal.terms.sections.termination.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.termination.items.user',
          'legal.terms.sections.termination.items.platform'
        ]
      }
    ]
  },
  {
    id: 'governingLaw',
    titleKey: 'legal.terms.sections.governingLaw.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.governingLaw.items.law',
          'legal.terms.sections.governingLaw.items.severability'
        ]
      }
    ]
  },
  {
    id: 'notices',
    titleKey: 'legal.terms.sections.notices.title',
    blocks: [
      {
        type: 'bullets',
        keys: [
          'legal.terms.sections.notices.items.channels',
          'legal.terms.sections.notices.items.userDuty'
        ]
      }
    ]
  }
];

export const legalDocuments: Record<LegalDocumentType, LegalDocumentDefinition> = {
  privacy: {
    type: 'privacy',
    heroTitleKey: 'legal.privacy.title',
    heroSummaryKey: 'legal.privacy.summary',
    sections: privacySections
  },
  terms: {
    type: 'terms',
    heroTitleKey: 'legal.terms.title',
    heroSummaryKey: 'legal.terms.summary',
    sections: termsSections
  }
};

export const legalDocumentRoutes: Record<string, LegalDocumentType> = {
  '/legal/privacy': 'privacy',
  '/legal/terms': 'terms'
};
